/**
 * @file PatientLayout.jsx
 *
 * Bottom-tab nav on mobile, top-tab nav on desktop. 4 sections: Mi Salud,
 * Mis Citas, Mis Profesionales, Mi Perfil.
 */
import React from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { Heart, CalendarDays, Users, User, LogOut } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { PATIENT_STRINGS } from "../constants/strings";
import "./PatientLayout.css";

const NAV_ITEMS = [
  { label: PATIENT_STRINGS.TAB_HOME, to: "/mi-salud", Icon: Heart },
  { label: PATIENT_STRINGS.TAB_APPOINTMENTS, to: "/mis-citas", Icon: CalendarDays },
  { label: PATIENT_STRINGS.TAB_MY_PROFESSIONALS, to: "/mis-profesionales", Icon: Users },
  { label: PATIENT_STRINGS.TAB_PROFILE, to: "/mi-perfil-paciente", Icon: User },
];

function isActive(path, item) {
  if (item.to === "/mi-salud") return path === "/mi-salud";
  return path === item.to || path.startsWith(`${item.to}/`);
}

function BrandMark() {
  return (
    <span className="pl-brand-mark" aria-hidden>
      <span />
    </span>
  );
}

export default function PatientLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="pl-shell">
      {/* Top brand bar (desktop + mobile) */}
      <header className="pl-topbar">
        <RouterLink to="/mi-salud" className="pl-brand">
          <BrandMark />
          <span className="pl-brand-name">Consultto</span>
        </RouterLink>
        <button type="button" className="pl-logout" onClick={handleLogout} aria-label="Cerrar sesión">
          <LogOut size={16} />
        </button>
      </header>

      {/* Desktop tab nav under topbar */}
      <nav className="pl-tabs">
        {NAV_ITEMS.map((item) => {
          const active = isActive(location.pathname, item);
          const Icon = item.Icon;
          return (
            <RouterLink
              key={item.to}
              to={item.to}
              className={`pl-tab ${active ? "pl-tab-active" : ""}`}
            >
              <Icon size={16} />
              <span>{item.label}</span>
            </RouterLink>
          );
        })}
      </nav>

      <main className="pl-main">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="pl-bottom" role="navigation" aria-label="Patient navigation">
        {NAV_ITEMS.map((item) => {
          const active = isActive(location.pathname, item);
          const Icon = item.Icon;
          return (
            <RouterLink
              key={item.to}
              to={item.to}
              className={`pl-bottom-item ${active ? "pl-bottom-item-active" : ""}`}
            >
              <Icon size={20} />
              <span>{item.label}</span>
            </RouterLink>
          );
        })}
      </nav>
    </div>
  );
}
